import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useCommunityInboxStore } from "@circle-react/hooks/chatsV2/store";
import { useWebSocket } from "@circle-react/hooks/useWebSocket";
import type { ChatType } from "@circle-react/types/CommunityBot/ChatSort";
import type { AdminChannelMessage } from "@circle-react/types/CommunityInbox/AdminChannelMessage";
import type { Chat } from "@circle-react/types/CommunityInbox/Chat";

interface UseCommunityInboxSyncOptions {
  activeChatType?: ChatType;
  agentId?: number;
}

export const useCommunityInboxSync = ({
  activeChatType,
  agentId = 0,
}: UseCommunityInboxSyncOptions = {}) => {
  const { currentCommunity } = usePunditUserContext();
  const communityId = currentCommunity?.id;
  const {
    appendMessage,
    updateMessage,
    destroyMessage,
    appendChat,
    updateChat,
    updateAgentChats,
    destroyChat,
    markAllAdminChatsAsRead,
  } = useCommunityInboxStore();

  const matchesAgentFilter = (chat: Chat) =>
    agentId === 0 || chat.community_bot_profile.id === agentId;

  return useWebSocket(
    {
      channel: "CommunityInbox::AdminChannel",
      community_id: communityId,
      canCreateConnection: !!communityId,
      onMessageReceive: (data: AdminChannelMessage) => {
        switch (data.event) {
          case "createMessage": {
            appendMessage(data.json_message, "admin");
            break;
          }
          case "updateMessage": {
            updateMessage(data.json_message);
            break;
          }
          case "destroyMessage": {
            destroyMessage(data.json_message.id);
            break;
          }
          case "createChat": {
            const chat = data.json_message;
            if (chat.has_member_interaction && matchesAgentFilter(chat)) {
              appendChat(chat, "admin");
            }
            break;
          }
          case "updateChat": {
            const chat = data.json_message;
            if (chat.has_member_interaction && matchesAgentFilter(chat)) {
              updateChat(chat, "admin");
            }
            break;
          }
          case "destroyChat": {
            destroyChat(data.json_message.id, "admin");
            break;
          }
          case "adminMarkAllChatsAsRead": {
            markAllAdminChatsAsRead(activeChatType);
            break;
          }
          case "updateAgentChats": {
            updateAgentChats(
              data.json_message.agent_id,
              data.json_message.enabled,
              data.json_message.chats,
              data.json_message.deleted,
            );
            break;
          }
          default:
            break;
        }
      },
    },
    [communityId, activeChatType, agentId],
  );
};
